import { useCallback, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";

import { Loader, Button } from "components/elements";
import {
  TARGET_FIELDS,
  TiVoDevicesModal,
} from "components/form/fields/TiVoTargets";
import {
  GrabsonIcon,
  Table,
  TableActions,
  TableBody,
  TableHeaderColumn,
  TableLoader,
  TableNoResults,
  TablePagination,
  TableRow,
  SubTab,
  SubTabs,
} from "components/layout";
import { Tooltip } from "components/popups";
import { TiVoPublications, TiVoTargets } from "hooks/tivoApps";
import {
  getOems,
  TiVoDeviceClientType,
  TiVoDeviceOem,
  TiVoDevicePlatform,
} from "hooks/tivoDevices";
import { useToggle } from "hooks/utils";

import { PAGE_LIMIT, TEXTS } from "../constants";
import styles from "./PublicationsTable.scss";
import {
  PublicationsTableRow,
  PublicationsTableRowProps,
} from "./PublicationsTableRow";

const BASE_COL_COUNT = 3;

export type PublicationsTableProps = Pick<
  PublicationsTableRowProps,
  "cellClassName" | "renderRowPanel" | "children"
> & {
  colClassName?: string;
  platforms: TiVoDevicePlatform[];
  publications: TiVoPublications[];
  status: {
    count: number;
    error: Error | null;
    loading: boolean;
  };
  targets: TiVoTargets;
  setTargets: (targets: TiVoTargets) => void;
};

export function PublicationsTable({
  colClassName,
  cellClassName,
  platforms,
  publications,
  status,
  targets,
  renderRowPanel,
  setTargets,
  children,
}: PublicationsTableProps) {
  const { pathname, search = "" } = useLocation();
  const { tabId: platformId } = useParams();

  const searchParams = useMemo(() => {
    const params = new URLSearchParams(search);
    params.delete("page");
    return `?${params.toString()}`;
  }, [search]);

  const [targetsModalOpen, toggleTargetsModalOpen] = useToggle(false);

  const targetByClientTypes = useMemo(
    () => platforms.find(({ id }) => id === platformId)?.targetedByClientType,
    [platformId, platforms]
  );

  const targetFields = useMemo(() => {
    if (targetByClientTypes) {
      return [TARGET_FIELDS.oems, TARGET_FIELDS.clientTypes];
    }
    return [TARGET_FIELDS.oems];
  }, [targetByClientTypes]);

  const getOemClientTypes = useCallback<
    (oem: TiVoDeviceOem) => TiVoDeviceClientType[]
  >(
    (oem) => {
      if (!targetByClientTypes) {
        return [];
      }
      let clientTypes = oem.clientTypes;
      if (!clientTypes) {
        return [];
      }
      if (
        clientTypes.some((clientType) =>
          targets.clientTypes?.includes(clientType.id)
        )
      ) {
        clientTypes = clientTypes.filter((clientType) =>
          targets.clientTypes?.includes(clientType.id)
        );
      }
      return clientTypes;
    },
    [targetByClientTypes, targets]
  );

  /*
   * Table
   */

  const fillRowSpan = targetByClientTypes ? 2 : 1;

  const oems = useMemo(() => getOems(platforms, targets), [platforms, targets]);

  const oemCols = useMemo(
    () =>
      oems.map((oem) => ({
        id: oem.id,
        label: oem.name,
        colSpan:
          getOemClientTypes(oem).length > 0 ? getOemClientTypes(oem).length : 1,
        rowSpan: getOemClientTypes(oem).length > 0 ? 1 : fillRowSpan,
      })),
    [fillRowSpan, oems, getOemClientTypes]
  );

  const clientTypeCols = useMemo(
    () =>
      oems
        .reduce<
          TiVoDeviceClientType[]
        >((acc, cur) => [...acc, ...getOemClientTypes(cur)], [])
        .map((clientType) => ({ id: clientType.id, label: clientType.name })),
    [oems, getOemClientTypes]
  );

  const loaderColSpan = useMemo(
    () => oemCols.reduce((acc, cur) => acc + cur.colSpan, BASE_COL_COUNT),
    [oemCols]
  );

  /*
   * Rendering
   */

  if (!platformId) {
    return null;
  }

  if (platforms.length === 0) {
    return status.loading ? <Loader /> : null;
  }

  const renderActions = () => (
    <TableActions>
      <TablePagination rowsPerPage={PAGE_LIMIT} totalCount={status.count} />
    </TableActions>
  );

  const renderBody = () => {
    if (status.error || status.loading || publications.length === 0) {
      const message = status.error
        ? status.error.message
        : TEXTS.NO_PUBLICATIONS;
      return (
        <TableNoResults
          colspan={loaderColSpan}
          isError={status.error}
          loading={status.loading}
          message={message}
        />
      );
    }
    return (
      <TableBody>
        {publications?.map((publication) => {
          const variant = publication.variant;
          const provider = variant?.provider;
          if (!provider || !variant) {
            return null;
          }
          return (
            <PublicationsTableRow
              key={publication.id}
              provider={provider}
              variant={variant}
              publication={publication}
              platformId={platformId}
              oems={oems}
              cellClassName={cellClassName}
              getOemClientTypes={getOemClientTypes}
              renderRowPanel={renderRowPanel}
            >
              {children}
            </PublicationsTableRow>
          );
        })}
      </TableBody>
    );
  };

  const renderHeader = () => (
    <thead>
      <TableRow highlighting={false}>
        <TableHeaderColumn
          className={styles.providerCell}
          rowspan={fillRowSpan}
        >
          {TEXTS.PROVIDER_COL}
        </TableHeaderColumn>
        <TableHeaderColumn className={styles.variantCell} rowspan={fillRowSpan}>
          {TEXTS.VARIANT_COL}
        </TableHeaderColumn>
        {oemCols.map((col) => (
          <TableHeaderColumn
            key={col.id}
            className={colClassName}
            colspan={col.colSpan}
            rowspan={col.rowSpan}
          >
            {col.label}
          </TableHeaderColumn>
        ))}
        <TableHeaderColumn className={styles.menuCell} rowspan={fillRowSpan}>
          <Tooltip
            alignment="left-start"
            content={
              targetByClientTypes
                ? TEXTS.SELECT_CLIENT_TYPES_BUTTON
                : TEXTS.SELECT_OEMS_BUTTON
            }
          >
            <Button
              className={styles.selectTargetButton}
              type="menuButton"
              dataTestId="select-target-button"
              onClick={toggleTargetsModalOpen}
            >
              <GrabsonIcon className={styles.icon} name="settings" />
            </Button>
          </Tooltip>
        </TableHeaderColumn>
      </TableRow>
      {targetByClientTypes && (
        <TableRow highlighting={false}>
          {clientTypeCols.map((col) => (
            <TableHeaderColumn key={col.id} className={colClassName}>
              {col.label}
            </TableHeaderColumn>
          ))}
        </TableRow>
      )}
      <TableLoader loading={status.loading} colspan={loaderColSpan} />
    </thead>
  );

  const renderTabs = () => (
    <SubTabs>
      {platforms.map((platform) => (
        <SubTab
          key={platform.id}
          active={platform.id === platformId}
          text={platform.name}
          to={`${pathname
            .split("/")
            .toSpliced(-1, 1, platform.id)
            .join("/")}${searchParams}`}
          dataTestId={`${platform.slug}-tab`}
        />
      ))}
    </SubTabs>
  );

  return (
    <>
      <div className={styles.container}>
        {renderActions()}
        {renderTabs()}
        <div className={styles.tableContainer}>
          <Table>
            {renderHeader()}
            {renderBody()}
          </Table>
        </div>
        {renderActions()}
      </div>
      <TiVoDevicesModal
        fields={targetFields}
        isOpen={targetsModalOpen}
        restrictTargets={{ platforms: [platformId] }}
        targets={targets}
        onClose={toggleTargetsModalOpen}
        onSave={(targets) => {
          setTargets(targets);
          toggleTargetsModalOpen();
        }}
      />
    </>
  );
}
